import {DataStatus, TabProps, Tabs} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {GetDeliveryNoteResponse, useGetReceiveNoteItemsTotalPriceQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {queryParams, Section} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {buildArray, Nullish, RequiredTestIdProps, suffixTestId, useQueryState} from 'shared/web';

import {GeneralInformationTab} from './components/GeneralInformationTab';
import {ListOfItemsTab} from './components/ListOfItemsTab';
import {TotalsTab} from './components/TotalsTab';

interface OverviewProps extends RequiredTestIdProps {
  deliveryNote: GetDeliveryNoteResponse | Nullish;
  isLoading: boolean;
  hasError: boolean;
}

export function Overview(props: OverviewProps) {
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const {deliveryNoteId} = useWarehouseParams();

  const receiveNoteId = props.deliveryNote?.receiveNote?.receiveNoteId;

  const {data: receiveNoteTotalPrice} = useGetReceiveNoteItemsTotalPriceQuery(
    {receiveNoteId: receiveNoteId as string},
    {skip: isNilOrEmpty(receiveNoteId)}
  );

  const deliveryNotePriceWithoutVat = props.deliveryNote?.totalPrice?.withoutVat?.amount ?? 0;
  const deliveryNotePriceWithVat = props.deliveryNote?.totalPrice?.withVat?.amount ?? 0;
  const receiveNotePriceWithoutVat = receiveNoteTotalPrice?.totalPriceWithoutVat ?? 0;
  const receiveNotePriceWithVat = receiveNoteTotalPrice?.totalPriceWithVat ?? 0;
  const checkingTotalsWithoutVat = receiveNotePriceWithoutVat - deliveryNotePriceWithoutVat;
  const checkingTotalsWithVat = receiveNotePriceWithVat - deliveryNotePriceWithVat;
  const hasTotalsDiscrepancy = checkingTotalsWithoutVat !== 0 || checkingTotalsWithVat !== 0;

  const tabs = buildArray<TabProps>()
    .add({
      title: i18n.t('entity.warehouse.labels.generalInformation'),
      id: 'generalInformation',
      content: (
        <GeneralInformationTab
          deliveryNoteId={deliveryNoteId}
          deliveryNote={props.deliveryNote}
          isLoading={props.isLoading}
          hasError={props.hasError}
          data-testid={suffixTestId('generalInformation', props)}
        />
      ),
    })
    .add({
      title: i18n.t('entity.warehouse.labels.listOfItems'),
      id: 'listOfItems',
      content: (
        <ListOfItemsTab
          deliveryNoteId={deliveryNoteId}
          deliveryNote={props.deliveryNote}
          data-testid={suffixTestId('listOfItems', props)}
        />
      ),
    })
    .add({
      title: i18n.t('entity.warehouse.labels.totals'),
      id: 'totals',
      content: (
        <TotalsTab
          deliveryNote={props.deliveryNote}
          receiveNoteTotalPrice={receiveNoteTotalPrice}
          currency={props.deliveryNote?.currency}
          data-testid={suffixTestId('totals', props)}
        />
      ),
      icon: hasTotalsDiscrepancy ? 'error' : 'success',
    });

  return (
    <Section isFullHeight data-testid={suffixTestId('wrapper', props)}>
      <DataStatus isLoading={props.isLoading} isError={props.hasError} minHeight="100vh">
        <Tabs
          isFullHeight
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
          isLazy
          variant="condensed"
        />
      </DataStatus>
    </Section>
  );
}
